import React from "react";
import { Grid, Box } from "@mui/material";
import { FaLightbulb, FaUsers, FaHeartbeat, FaComments } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { FaProjectDiagram } from "react-icons/fa";

/*
 * TODO: Enhanced skill-project interaction
 * Future enhancement: Instead of linking to projects page, implement an inline
 * project carousel that appears when a skill is clicked.
 *
 * Implementation plan:
 * 1. Add useState for selectedSkill
 * 2. Create ProjectCarousel component that filters projects by tech
 * 3. Layout: skills grid on left (desktop) / top (mobile), carousel on right/bottom
 * 4. Highlight selected skill, show matching projects with smooth animation
 *
 * State needed: const [selectedSkill, setSelectedSkill] = useState(null);
 * Filter logic: projects.filter(p => p.techStack.includes(selectedSkill))
 */

const techSkills = [
  // Row 1
  { alt: "Java Logo", src: "/images/java.png", tech: "Java" },
  { alt: "React Logo", src: "/images/React-Logo-PNG-Image-File.png", tech: "React" },
  { alt: "C# Logo", src: "/images/C_Sharp_Logo_2023.svg.png", tech: "C#" },
  { alt: "JavaScript Logo", src: "/images/javascript-vector-logo-yellow-png-transparent-javascript-vector-12.png", tech: "JavaScript" },
  // Row 2
  { alt: "PHP Logo", src: "/images/php-logo-bigger.png", tech: "PHP" },
  { alt: "Laravel Logo", src: "/images/laravel-logo-png-laravel-lumen-manipulating-route-parameters-syed-sirajul-islam-1024x400.png", tech: "Laravel" },
  { alt: "Node.js Logo", src: "/images/js-logo-node-logos-and-brands-icon.png", tech: "Node.js" },
  { alt: "SQL Logo", src: "/images/sql-database-icon-png-17.png", tech: "MySQL" },
  // Row 3
  { alt: "Docker Logo", src: "/images/logos/docker.jpg", tech: null },
  { alt: "Git Logo", src: "/images/logos/git-bash-logo-png_seeklogo-412974.png", tech: null },
  { alt: "AWS Certificate", src: "/images/aws-academy-graduate-aws-academy-cloud-security-foundations.png", tech: null },
  { alt: "Postman Logo", src: "/images/postman-api-platform-logo-png_seeklogo-446859.png", tech: "Postman" },
  // Row 4
  { alt: "Dart Logo", src: "/images/logos/Dart.jpg", tech: "Dart" },
  { alt: "Flutter Logo", src: "/images/logos/flutter.webp", tech: "Flutter" },
  { alt: "Bootstrap Logo", src: "/images/bootstrap-logo-png-bootstrap-logo-390.png", tech: "Bootstrap" },
  { alt: "Figma Logo", src: "/images/figma.png", tech: "Figma" },
]

const softSkills = [
  { labelKey: "scrum_leader", icon: <FaProjectDiagram /> },
  { labelKey: "problem_solver", icon: <FaLightbulb /> },
  { labelKey: "multicultural", icon: <FaUsers /> },
  { labelKey: "under_pressure", icon: <FaHeartbeat /> },
  { labelKey: "communication", icon: <FaComments /> }
];
const languages = [
  { name: "English", level: 100 },
  { name: "French", level: 100 },
  { name: "Russian", level: 100 },
  { name: "Spanish", level: 75 },
  {
    name: "Track my language learning progress on Duolingo",
    level: 0,
    link: "https://www.duolingo.com/profile/a_blue_kettle"
  },
  { name: "Romanian", level: 25 },
];

export default function Skills() {

    const { t } = useTranslation();

  return (
    <div id="content">
       {/* =============== TECHNICALLY SKILLS =============== */}

       <section className="tech_skills py-5 text-center">
  <h3>{t('skills.technical_skills')}</h3>
  <p>{t('skills.tech_description')}</p>

  <div className="tech-skills-grid">
    {techSkills.map((skill, idx) => (
      skill.tech ? (
        <Link
          to={`/en/projects?tech=${encodeURIComponent(skill.tech)}`}
          className="tech-skill"
          key={idx}
          title={`View ${skill.tech} projects`}
        >
          <img src={skill.src} alt={t(skill.alt) || skill.alt} />
        </Link>
      ) : (
        <div className="tech-skill" key={idx}>
          <img src={skill.src} alt={t(skill.alt) || skill.alt} />
        </div>
      )
    ))}
  </div>
</section>

       {/* =============== SOFT SKILLS =============== */}
      <section id="soft_skills" className="py-5">
        <h3 className="text-center">{t('skills.soft_skills')}</h3>
        <p className="text-center">{t('skills.soft_description')}</p>
        <div className="transparent-background-cold py-3">
          <ul className="list-unstyled text-center">
            {softSkills.map((skill, idx) => (
              <li key={idx} className="skill my-2 d-flex align-items-center justify-content-center gap-2">
                <span className="fs-5 text-primary">{skill.icon}</span>
                <span>{t(`soft_skill_labels.${skill.labelKey}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

       {/* =============== LANGUAGES =============== */}
      <section id="languages" className="py-5">
        <h3 className="text-center">{t('skills.languages_i_speak')}</h3>
        <div className="transparent-background-warm py-3">
          <div className="container">
           <ul className="list-unstyled d-flex flex-wrap justify-content-center gap-4">
  {languages.map((lang, idx) => (
    <li key={idx} className="language w-100 text-center" style={{ maxWidth: "300px" }}>
      {lang.link ? (
        <a href={lang.link} target="_blank" rel="noopener noreferrer">
          {lang.name}
        </a>
      ) : (
        <>
          <strong>{lang.name}</strong>
          <div className="progress mt-2">
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${lang.level}%` }}
              aria-valuenow={lang.level}
              aria-valuemin="0"
              aria-valuemax="100"
            />
          </div>
        </>
      )}
    </li>
  ))}
</ul>
          </div>
        </div>
      </section>
    </div>
  );
}